import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import useBlogStore from "../stores/blogs/blogs";
import useBlogCategoryStore from "../stores/blogs/categories";
import { useToast } from "../context/toastContext";
import SuccessModal from "../components/modal/SuccessModal";
import { useRef } from "react";
import { authenticatedAxios } from "../apis";
import remarkGfm from "remark-gfm";

// --- Helper Components ---
const InputGroup = ({ label, children, className = "", hint }) => (
  <div className={`mb-5 ${className}`}>
    <label className="text-left block text-sm font-semibold text-gray-800 mb-2">
      {label}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1.5 text-left">{hint}</p>}
  </div>
);

const Card = ({ title, children, className = "" }) => (
  <div
    className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${className}`}
  >
    {title && (
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <h2 className="text-left text-lg font-bold text-gray-900">{title}</h2>
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

export default function ArticleEditManagement() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [showSuccess, setShowSuccess] = useState(false);
  const [tempImages, setTempImages] = useState({});
  let imageCounterRef = useRef(1);
  const [isPublishing, setIsPublishing] = useState(false);

  const language = location.pathname.startsWith("/kr") ? "kr" : "en";

  const {
    articleDetail,
    markdown,
    loadingDetail,
    loadingMarkdown,
    loadingAction,
    actions: { fetchDetail, fetchMarkdown, updateBlog },
  } = useBlogStore();
  const textareaRef = useRef(null);

  const categories = useBlogCategoryStore((s) => s.categories);
  const loadingCategories = useBlogCategoryStore((s) => s.loading);
  const fetchCategories = useBlogCategoryStore(
    (s) => s.actions.fetchCategories,
  );

  // --- Local State ---
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    metaTitle: "",
    metaDescription: "",
    keywords: [],
    thumbnail: null,
    heroImage: null,
  });
  const [localCategories, setLocalCategories] = useState([]);
  const [localMarkdown, setLocalMarkdown] = useState("");
  const [newImages, setNewImages] = useState([]);
  const [isCategoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState("split"); // split, editor, preview

  // --- Initial Data Fetching ---
  useEffect(() => {
    fetchCategories();
    fetchDetail(slug, language);
    fetchMarkdown(slug, language);
  }, [slug, language, fetchCategories, fetchDetail, fetchMarkdown]);

  // --- Sync State with Store ---
  useEffect(() => {
    if (articleDetail) {
      setFormData({
        title: articleDetail.title || "",
        excerpt: articleDetail.excerpt || "",
        metaTitle: articleDetail.metaTitle || "",
        metaDescription: articleDetail.metaDescription || "",
        keywords: articleDetail.keywords || [],
        thumbnail: articleDetail.thumbnail,
        heroImage: articleDetail.heroImage,
      });

      setLocalCategories(
        articleDetail.categories || [articleDetail.category].filter(Boolean),
      );
    }
  }, [articleDetail]);

  const insertAtCursor = (text) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const before = localMarkdown.substring(0, start);
    const after = localMarkdown.substring(end);

    const newValue = before + text + after;

    setLocalMarkdown(newValue);

    // Move cursor
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
    }, 0);
  };

  const handleMarkdownTempImage = async (file) => {
    const MAX_SIZE = 3 * 1024 * 1024; // 3MB

    // Reject if file too large
    if (file.size > MAX_SIZE) {
      addToast("error", "Image is too large. Max 3MB allowed.");
      return;
    }

    const key = `image-${imageCounterRef.current++}`;
    const blobUrl = URL.createObjectURL(file);

    setTempImages((prev) => ({
      ...prev,
      [key]: blobUrl,
    }));

    insertAtCursor(`\n\n{{${key}}}\n\n`);
  };

  useEffect(() => {
    if (markdown) setLocalMarkdown(markdown);
  }, [markdown]);

  // --- Handlers ---
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleKeywordChange = (value) => {
    const keywordsArray = value.split(",").map((k) => k.trim());
    setFormData((prev) => ({ ...prev, keywords: keywordsArray }));
  };

  const toggleCategory = (catName) => {
    setLocalCategories((prev) =>
      prev.includes(catName)
        ? prev.filter((c) => c !== catName)
        : [...prev, catName],
    );
  };

  const handleSave = async () => {
    if (!formData.title || !localMarkdown) {
      addToast("error", "Please fill all required fields (title & content).");
      return;
    }

    setIsPublishing(true);

    try {
      // Step 1: Upload all temporary images and get their URLs
      let finalMarkdown = localMarkdown;

      for (const [key, blobUrl] of Object.entries(tempImages)) {
        try {
          // Skip upload if placeholder not found in markdown
          if (!finalMarkdown.includes(`{{${key}}}`)) {
            continue;
          }

          // Convert blob URL back to file
          const response = await fetch(blobUrl);
          const blob = await response.blob();
          const file = new File([blob], `${key}.jpg`, { type: blob.type });

          // Upload to server
          const fd = new FormData();
          fd.append("blog_image", file);

          const { data } = await authenticatedAxios.post(
            "/api/admin/blog/content/upload-image",
            fd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );

          if (data?.url) {
            // Replace placeholder with actual URL
            finalMarkdown = finalMarkdown.replace(
              new RegExp(`\\{\\{${key}\\}\\}`, "g"),
              `![image](${data.url})`,
            );
            // addToast("success", `Image ${key} uploaded successfully!`);
          }
        } catch (err) {
          console.error(`Failed to upload ${key}:`, err);
          addToast("error", `Failed to upload image ${key}`);
        }
      }

      const mdBlob = new Blob([finalMarkdown], { type: "text/markdown" });
      const mdFile = new File([mdBlob], `${slug}.md`, {
        type: "text/markdown",
      });

      const fd = new FormData();
      fd.append("slug", slug);
      fd.append("title", formData.title);
      fd.append("excerpt", formData.excerpt);
      fd.append("metaTitle", formData.metaTitle);
      fd.append("metaDescription", formData.metaDescription);
      fd.append("keywords", JSON.stringify(formData.keywords));
      fd.append(
        "categories",
        JSON.stringify(
          localCategories.map((cat) => {
            const id = categories.find((ct) => cat === ct.name)?.id;
            return id;
          }),
        ),
      );
      //   fd.append("category", localCategories[0] || "");
      fd.append("language", language);
      fd.append("markdown", mdFile);

      if (formData.thumbnail && typeof formData?.thumbnail !== "string")
        fd.append("thumbnail", formData.thumbnail);
      //   if (formData.heroImage) fd.append("heroImage", formData.heroImage);

      newImages.forEach((img) => fd.append("images", img));

      const ok = await updateBlog(slug, fd, language);
      if (ok) {
        setShowSuccess(true);

        setTimeout(() => {
          setShowSuccess(false);
          navigate("/article");
        }, 1500);
      } else {
        addToast("error", "Failed to update article.");
      }
    } catch (error) {
      console.error("Save error:", error);
      addToast("error", "Unexpected error occurred.");
    } finally {
      setIsPublishing(false);
    }
  };

  // --- Render: Loading ---
  if (loadingDetail || loadingMarkdown || !articleDetail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="max-w-screen-2xl mx-auto space-y-6">
          <div className="h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl w-1/3 animate-pulse"></div>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <div className="xl:col-span-4 space-y-6">
              <div className="h-96 bg-white rounded-2xl animate-pulse"></div>
              <div className="h-64 bg-white rounded-2xl animate-pulse"></div>
            </div>
            <div className="xl:col-span-8 h-[85vh] bg-white rounded-2xl animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  // --- Render: Main ---
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      <SuccessModal
        show={showSuccess}
        message="Article updated successfully!"
        onClose={() => setShowSuccess(false)}
      />

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Edit Article
                </h1>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    language === "en"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-purple-100 text-purple-700"
                  }`}
                >
                  {language.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
                <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs">
                  {slug}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => navigate(-1)}
                className="px-5 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isPublishing}
                className={`px-6 py-2.5 rounded-xl text-white font-semibold flex items-center gap-2 ${
                  isPublishing
                    ? "bg-blue-400 cursor-not-allowed"
                    : " bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isPublishing ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* LEFT SIDEBAR: Settings & Metadata */}
          <div className="xl:col-span-4 space-y-6">
            {/* General Info Card */}
            <Card title="General Information">
              <InputGroup
                label="Title"
                hint="The main headline of your article"
              >
                <input
                  type="text"
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all hover:border-gray-300"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder="Enter article title..."
                />
              </InputGroup>

              <InputGroup
                label="Excerpt"
                hint="Brief summary shown in article listings"
              >
                <textarea
                  rows={4}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all hover:border-gray-300 resize-none"
                  value={formData.excerpt}
                  onChange={(e) => handleInputChange("excerpt", e.target.value)}
                  placeholder="Write a compelling excerpt..."
                />
              </InputGroup>

              <InputGroup
                label="Categories"
                hint="Select one or more categories"
              >
                <div className="relative">
                  <div
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 bg-white cursor-pointer min-h-[52px] flex flex-wrap gap-2 items-center hover:border-gray-300 transition-all"
                    onClick={() =>
                      setCategoryDropdownOpen(!isCategoryDropdownOpen)
                    }
                  >
                    {localCategories.length > 0 ? (
                      localCategories.map((cat) => (
                        <span
                          key={cat}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs px-3 py-1.5 rounded-lg font-semibold shadow-sm"
                        >
                          {cat}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400 flex items-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                          />
                        </svg>
                        Select categories...
                      </span>
                    )}
                  </div>

                  {isCategoryDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setCategoryDropdownOpen(false)}
                      />
                      <div className="absolute top-full mt-2 left-0 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl z-20 max-h-64 overflow-y-auto">
                        <div className="p-2">
                          {categories.map((cat) => (
                            <label
                              key={cat.id}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 rounded-lg cursor-pointer transition-all group"
                            >
                              <input
                                type="checkbox"
                                className="w-5 h-5 text-blue-600 rounded-md focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                checked={localCategories.includes(cat.name)}
                                onChange={() => toggleCategory(cat.name)}
                              />
                              <span className="text-gray-700 font-medium group-hover:text-blue-700 transition-colors">
                                {cat.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </InputGroup>
            </Card>

            {/* SEO Card */}
            <Card title="SEO Settings">
              <InputGroup
                label="Meta Title"
                hint="Optimized for search engines (50-60 chars)"
              >
                <input
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all hover:border-gray-300"
                  value={formData.metaTitle}
                  onChange={(e) =>
                    handleInputChange("metaTitle", e.target.value)
                  }
                  placeholder="SEO-friendly title..."
                />
              </InputGroup>

              <InputGroup
                label="Meta Description"
                hint="150-160 characters recommended"
              >
                <textarea
                  rows={3}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all hover:border-gray-300 resize-none"
                  value={formData.metaDescription}
                  onChange={(e) =>
                    handleInputChange("metaDescription", e.target.value)
                  }
                  placeholder="Description for search results..."
                />
              </InputGroup>

              <InputGroup label="Keywords" hint="Separate with commas">
                <input
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all hover:border-gray-300"
                  value={formData.keywords.join(", ")}
                  onChange={(e) => handleKeywordChange(e.target.value)}
                  placeholder="react, javascript, web dev"
                />
              </InputGroup>
            </Card>

            {/* Assets Card */}
            <Card title="Assets">
              <InputGroup
                label="Upload Thumbnail"
                // hint="Support for multiple images"
              >
                <input
                  type="file"
                  //   multiple
                  accept="image/*"
                  className="block w-full text-sm text-gray-600 file:mr-4 file:py-3 file:px-5 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-gradient-to-r file:from-blue-50 file:to-blue-100 file:text-blue-700 hover:file:from-blue-100 hover:file:to-blue-200 transition-all cursor-pointer"
                  onChange={(e) => setNewImages(Array.from(e.target.files))}
                />
              </InputGroup>

              {newImages.length > 0 && (
                <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                  <p className="text-xs font-bold text-green-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Ready to upload ({newImages.length})
                  </p>
                  <div className="space-y-2">
                    {newImages.map((file, idx) => (
                      <div
                        key={idx}
                        className="text-sm text-green-700 flex items-center gap-2 bg-white px-3 py-2 rounded-lg"
                      >
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        <span className="truncate">{file.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* RIGHT PANEL: Markdown Editor */}
          <div className="xl:col-span-8">
            <Card className="h-fit">
              {/* Editor Controls */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  Content Editor
                </h2>

                <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                  {[
                    {
                      mode: "split",
                      label: "Split",
                      icon: "M9 4H5a2 2 0 00-2 2v14a2 2 0 002 2h4m0-18v18m0-18l6-6m-6 6l-6-6m12 0h4a2 2 0 012 2v14a2 2 0 01-2 2h-4",
                    },
                    {
                      mode: "editor",
                      label: "Edit",
                      icon: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
                    },
                    {
                      mode: "preview",
                      label: "Preview",
                      icon: "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
                    },
                  ].map(({ mode, label, icon }) => (
                    <button
                      key={mode}
                      onClick={() => setPreviewMode(mode)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                        previewMode === mode
                          ? "bg-white text-blue-600 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={icon}
                        />
                      </svg>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-full flex mb-3">
                <input
                  type="file"
                  accept="image/*"
                  id="markdown-temp-image"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0])
                      handleMarkdownTempImage(e.target.files[0]);
                  }}
                />

                <button
                  className="px-4 py-2 !text-sm bg-white border rounded-md"
                  onClick={() =>
                    document.getElementById("markdown-temp-image").click()
                  }
                >
                  Insert Image
                </button>
              </div>

              {/* Editor/Preview Area */}
              <div
                className={`grid gap-6 ${
                  previewMode === "split" ? "grid-cols-2" : "grid-cols-1"
                }`}
              >
                {/* Editor */}
                {(previewMode === "split" || previewMode === "editor") && (
                  <div className="flex flex-col">
                    <textarea
                      ref={textareaRef}
                      className="w-full h-[75vh] border-2 border-gray-200 rounded-xl p-5 font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none bg-gray-50 hover:bg-white"
                      value={localMarkdown}
                      onChange={(e) => setLocalMarkdown(e.target.value)}
                      placeholder="# Start writing your masterpiece...&#10;&#10;Use **markdown** syntax to format your content."
                    />
                  </div>
                )}

                {/* Preview */}
                {(previewMode === "split" || previewMode === "preview") && (
                  <div className="flex flex-col">
                    <div
                      className="w-full h-[75vh] text-left overflow-y-auto border-2 border-gray-200 rounded-xl p-8 bg-white 
  prose prose-lg max-w-none
  prose-headings:font-bold 
  prose-h1:mb-6 prose-h1:mt-0 prose-h1:text-3xl
  prose-h2:mb-4 prose-h2:mt-10 prose-h2:text-2xl
  prose-h3:mb-3 prose-h3:mt-8 prose-h3:text-xl
  prose-p:leading-relaxed prose-p:mb-4
  prose-li:mb-2 prose-ul:mt-2 prose-ol:mt-2
  prose-strong:text-gray-900
  prose-a:text-blue-600 prose-a:underline
  prose-img:rounded-xl prose-img:shadow
  prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded
  prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-xl
"
                    >
                      {localMarkdown ? (
                        // <ReactMarkdown
                        //   rehypePlugins={[
                        //     rehypeRaw,
                        //     [rehypeSanitize, REHYPE_SCHEMA],
                        //   ]}
                        // >
                        //   {localMarkdown}
                        // </ReactMarkdown>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[
                            rehypeRaw,
                            [
                              rehypeSanitize,
                              {
                                ...defaultSchema,
                                attributes: {
                                  ...defaultSchema.attributes,
                                  "*": ["style", "className"],
                                },
                              },
                            ],
                          ]}
                          components={{
                            img: ({ src, alt }) => {
                              const resolvedSrc =
                                tempImages[src?.replace(/^\{\{|\}\}$/g, "")] ||
                                src;
                              return (
                                <img
                                  src={resolvedSrc}
                                  alt={alt}
                                  style={{
                                    maxWidth: "100%",
                                    borderRadius: 12,
                                    margin: "20px 0",
                                  }}
                                />
                              );
                            },
                          }}
                        >
                          {localMarkdown.replace(
                            /\{\{(image-\d+)\}\}/g,
                            (_, key) =>
                              tempImages[key]
                                ? `![image](${tempImages[key]})`
                                : `{{${key}}}`,
                          )}
                        </ReactMarkdown>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                          <div className="text-center">
                            <svg
                              className="w-16 h-16 mx-auto mb-4 opacity-50"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <p className="text-lg font-medium">
                              Preview will appear here
                            </p>
                            <p className="text-sm mt-2">
                              Start typing in the editor to see your content
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
